import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ScrollView, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
  CollectionHero,
  CollectionTopBar,
  GuardianPanel,
  RelicStorePanel,
} from './store/StoreView';
import PurchaseModal from './store/PurchaseModal';
import VaultBrowser from './vault/VaultBrowser';
import RelicDetailModal from './vault/RelicDetailModal';
import BadgesView from './badges/BadgesView';
import BadgeDetailModal from './badges/BadgeDetailModal';
import { useStorePurchaseFlow } from './hooks/useStorePurchaseFlow';
import { useVaultBrowser } from './hooks/useVaultBrowser';
import { useRelicVaultInsets } from './shared/safeArea';
import { useRelicVaultLayout } from './shared/useRelicVaultLayout';
import { pageBackground } from './shared/theme';
import { DEFAULT_COLLECTION_TAB } from './collectionTabs';
import {
  equipCompanion,
  refreshAllowanceFor,
  refreshStoreSlots,
} from 'store/relicVault';
import { ALL_GUILDS_ID, relicsForGuild } from 'utils/relicVault/vaultSelectors';

/**
 * The Collection Store, opened full-screen from the metaverse HUD. Three tabs:
 *
 *   Guardians  the nine companions, bought with QuikCoins and equipped here
 *   Relics     the five store slots, with the full relic collection below them
 *   Badges     every badge in the catalog, earned by playing rather than bought
 *
 * `balance` is the QuikCoins balance to display and spend against - the real
 * balance plus any local spend adjustment (see store/relicVault).
 */
const RelicVaultOverlay = ({ visible, onClose, balance, userId, onServerBalanceChanged }) => {
  const dispatch = useDispatch();
  const vault = useSelector(state => state.relicVault);
  const [tab, setTab] = useState(DEFAULT_COLLECTION_TAB);
  const [selectedBadgeId, setSelectedBadgeId] = useState(null);
  const { isTablet, isPhone } = useRelicVaultLayout();
  // Keep both top bars clear of the status bar / notch, so their close button
  // stays tappable. Padding the root means the page background still paints
  // behind the inset instead of leaving a bare strip.
  const insets = useRelicVaultInsets();
  // The store panel's "My Vault" chest scrolls down to the collection below it.
  const scrollRef = useRef(null);
  const collectionY = useRef(0);

  const purchase = useStorePurchaseFlow({ userId, balance, onServerBalanceChanged });
  const browser = useVaultBrowser({ userId, onServerBalanceChanged });

  // Always reopen on the first tab.
  useEffect(() => {
    if (visible) {
      setTab(DEFAULT_COLLECTION_TAB);
      setSelectedBadgeId(null);
    }
  }, [visible]);

  const refreshAllowance = refreshAllowanceFor(vault);

  const selectedRelic = useMemo(() => {
    if (!browser.selectedRelicId) return null;
    return (
      relicsForGuild(ALL_GUILDS_ID, vault.ownedRelicIds).find(
        r => r.id === browser.selectedRelicId,
      ) || null
    );
  }, [browser.selectedRelicId, vault.ownedRelicIds]);

  const onRefresh = useCallback(async () => {
    const result = await dispatch(refreshStoreSlots({ userId, balance }));
    if (!result?.success) {
      console.warn('[RelicVault] refresh failed', result?.error);
      return;
    }
    if (result.serverBalanceChanged && onServerBalanceChanged) onServerBalanceChanged();
  }, [balance, dispatch, onServerBalanceChanged, userId]);

  const onEquip = useCallback(
    guildId => dispatch(equipCompanion({ userId, guildId })),
    [dispatch, userId],
  );

  const handleClose = useCallback(() => {
    purchase.close();
    browser.closeDetail();
    setSelectedBadgeId(null);
    onClose && onClose();
  }, [browser, onClose, purchase]);

  return (
    <Modal
      visible={!!visible}
      animationType="fade"
      transparent
      onRequestClose={handleClose}
      supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}>
      <View
        style={[
          styles.root,
          pageBackground,
          {
            paddingTop: insets.top,
            paddingBottom: insets.bottom,
            paddingLeft: insets.left,
            paddingRight: insets.right,
          },
        ]}>
        {/* Fixed above the scroll view so the tabs, coins and close stay reachable. */}
        <CollectionTopBar
          balance={balance}
          coinGlow={browser.coinGlow}
          tab={tab}
          onTabChange={setTab}
          onClose={handleClose}
        />

        <ScrollView
          ref={scrollRef}
          style={styles.fill}
          contentContainerStyle={styles.scrollContent}>
          <View
            style={[
              styles.content,
              isTablet && styles.contentTablet,
              isPhone && styles.contentPhone,
            ]}>
            <CollectionHero />

            {tab === 'guardians' && (
              <GuardianPanel
                vault={vault}
                balance={balance}
                purchase={purchase}
                onEquip={onEquip}
              />
            )}

            {tab === 'relics' && (
              <>
                <RelicStorePanel
                  vault={vault}
                  balance={balance}
                  refreshAllowance={refreshAllowance}
                  purchase={purchase}
                  onRefresh={onRefresh}
                  onOpenVault={() =>
                    scrollRef.current?.scrollTo({ y: collectionY.current, animated: true })
                  }
                />
                <View
                  style={styles.collectionSection}
                  onLayout={e => {
                    collectionY.current = e.nativeEvent.layout.y;
                  }}>
                  <VaultBrowser vault={vault} browser={browser} />
                </View>
              </>
            )}

            {tab === 'badges' && (
              <BadgesView
                ownedBadgeIds={vault.ownedBadgeIds}
                onSelect={setSelectedBadgeId}
              />
            )}
          </View>
        </ScrollView>

        {tab !== 'badges' && (
          <PurchaseModal
            flow={purchase}
            balance={balance}
            ownedCompanionIds={vault.ownedCompanionIds}
            equippedCompanionId={vault.equippedCompanionId}
          />
        )}
        {tab === 'relics' && <RelicDetailModal relic={selectedRelic} browser={browser} />}
        {tab === 'badges' && (
          <BadgeDetailModal
            badgeId={selectedBadgeId}
            owned={vault.ownedBadgeIds.includes(selectedBadgeId)}
            onClose={() => setSelectedBadgeId(null)}
          />
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  fill: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    width: '100%',
    maxWidth: 1360,
    alignSelf: 'center',
    paddingTop: 24,
    paddingHorizontal: 32,
    paddingBottom: 80,
  },
  contentTablet: {
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 64,
  },
  contentPhone: {
    paddingTop: 16,
    paddingHorizontal: 14,
    paddingBottom: 56,
  },
  // The relic collection sits directly under the store slots on the Relics tab.
  collectionSection: {
    marginTop: 34,
  },
});

export default RelicVaultOverlay;
